import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Tilt from "react-parallax-tilt";
import SocialMedia from "../SocialMedia/SocialMedia";
import laundryLine from "../../Assets/laundry-line.webp";
import './Contact.scss';
import Seo, { breadcrumbSchema, organizationSchema, webPageSchema } from "../Seo/Seo";
import {
  CONTACT_FORM_EMAIL,
  CONTACT_FORM_SUCCESS_MESSAGE,
  submitContactForm,
} from "../../utils/contactFormSubmission";

function ContactForm() {
  const [status, setStatus] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setStatus(null);
    setEmailError("");

    const formData = new FormData(event.target);
    const email = formData.get("email");

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    formData.append("from_name", "Klean Leaf Website");
    formData.append("subject", "Klean Leaf - New Contact Form Submission");

    setIsSubmitting(true);
    try {
      await submitContactForm(formData);
      setStatus({ type: "success" });
      event.target.reset();
    } catch (error) {
      console.error("Contact form submission failed:", error);
      setStatus({ type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid className="contact-section primary page-top-settings">
      <Seo
        title="Request Private Label Laundry Sheet Samples | Klean Leaf"
        description="Request private label laundry detergent sheet samples, wholesale laundry sheet quotes, custom packaging support, and buyer assistance from Klean Leaf."
        path="/contact"
        schema={[
          organizationSchema,
          webPageSchema({
            name: "Request Private Label Laundry Sheet Samples | Klean Leaf",
            description: "Request private label laundry detergent sheet samples, wholesale laundry sheet quotes, custom packaging support, and buyer assistance from Klean Leaf.",
            path: "/contact",
            type: "ContactPage",
            primaryImage: laundryLine,
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <Container>
        <Row>
          <h1 className="center-text">Request Laundry Detergent Sheet Samples</h1>
          <h2 className="page-title">
            Start a Private Label Project
          </h2>
          <p className="page-subtitle">Tell us about your brand, target pack format, formula needs, and launch timeline.</p>
        </Row>
        <Row>
          <Col md={6} className="kitty">
            <Tilt>
              <img
                src={laundryLine}
                alt="Laundry line illustration for private label detergent sheet samples"
                className="img-fluid"
                decoding="async"
              />
            </Tilt>
          </Col>
          <Col md={5} className="contact-right equal-height-cards">
            <form onSubmit={onSubmit} className="contact-form">
              <label htmlFor="contact-name">Name *</label>
              <input id="contact-name" type="text" name="name" autoComplete="name" required />

              <label htmlFor="contact-email">Email *</label>
              <input
                id="contact-email"
                type="email"
                name="email"
                autoComplete="email"
                required
                aria-describedby={emailError ? "contact-email-error" : undefined}
                aria-invalid={emailError ? "true" : "false"}
                onChange={(e) => {
                  setEmailError(validateEmail(e.target.value) ? "" : "Please enter a valid email address.");
                }}
                onFocus={() => setEmailError("")}
              />
              {emailError && <span id="contact-email-error" className="error-message">{emailError}</span>}

              <label htmlFor="contact-company">Company</label>
              <input id="contact-company" type="text" name="company" autoComplete="organization" />

              <label htmlFor="contact-message">Message</label>
              <textarea id="contact-message" name="message" autoComplete="off" required maxLength="2000"></textarea>

              <button type="submit" className="submit-button primary-btn" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Request Samples or Quote"}
              </button>
              {status && (
                <span className={`form-result ${status.type}`}>
                  {status.type === "success" ? (
                    CONTACT_FORM_SUCCESS_MESSAGE
                  ) : (
                    <>
                      We couldn’t send your message right now. Please try again, or email us directly at{" "}
                      <a href={`mailto:${CONTACT_FORM_EMAIL}`}>{CONTACT_FORM_EMAIL}</a>.
                    </>
                  )}
                </span>
              )}
            </form>
          </Col>
        </Row>

        <Row className="mt-5">
          <h2>Talk to Our Columbus, Ohio Team</h2>
          <p>Email <strong>hello@kleanleaf.us</strong> or use the form to request samples, a laundry sheet quote, packaging options, retail package MOQ details, MSDS/testing documents, or logistics support.</p>
          <p>Our Office: Columbus, Ohio</p>
          <p>Helpful details to include: target market, pack count, fragrance-free or scented preference, packaging type, estimated order quantity, and desired launch date.</p>
          <p>Follow us on:</p>
          <SocialMedia />
        </Row>
      </Container>
    </Container>
  );
}

export default ContactForm;
